<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Field } from 'vee-validate';
import { OtpChannelType } from '@/shared/domain/OtpChannelType';

const model = defineModel<string>({ default: '' });

const props = withDefaults(defineProps<{
	digitsLength?: number;
	label?: string;
	name?: string;
  channel: OtpChannelType;
	required?: boolean;
}>(), {
  digitsLength: 6,
  label: 'Validation code',
  name: 'challenge-code',
  required: true
});

const digits = ref<Array<string>>(Array.from({ length: props.digitsLength }, () => ''));
const inputRefs = ref<Array<HTMLInputElement | null>>([]);

const codeValue = computed({
  get: () => model.value,
  set: (value: string) => {
    model.value = value;
  }
});

const fillDigitsFromValue = (value: string): void => {
  const sanitized = value.replace(/\D/g, '').slice(0, props.digitsLength);
  const nextDigits = Array.from({ length: props.digitsLength }, (_, index) => sanitized[index] ?? '');

  digits.value = nextDigits;
};

watch(() => model.value, (value) => {
  fillDigitsFromValue(value ?? '');
}, { immediate: true });

const setInputRef = (element: HTMLInputElement | null, index: number): void => {
  inputRefs.value[index] = element;
};

const syncValue = (): void => {
  codeValue.value = digits.value.join('');
};

const focusInput = (index: number): void => {
  const input = inputRefs.value[index];

  if (!input) {
    return;
  }

  input.focus();
  input.select();
};

const applyPastedDigits = (pastedText: string, index: number): void => {
  const pastedDigits = pastedText.replace(/\D/g, '').slice(0, props.digitsLength).split('');

  if (!pastedDigits.length) {
    return;
  }

  let cursor = index;

  pastedDigits.forEach((digit) => {
    if (cursor < props.digitsLength) {
      digits.value[cursor] = digit;
      cursor += 1;
    }
  });

  syncValue();

  focusInput(Math.min(cursor, props.digitsLength - 1));
};

const handleInput = (event: Event, index: number): void => {
  const input = event.target as HTMLInputElement;
  const digit = input.value.replace(/\D/g, '').slice(-1);

  digits.value[index] = digit;
  input.value = digit;
  syncValue();

  if (digit && index < props.digitsLength - 1) {
    focusInput(index + 1);
  }
};

const handleKeydown = (event: KeyboardEvent, index: number): void => {
  if (event.key === 'Backspace') {
    if (digits.value[index]) {
      digits.value[index] = '';
      syncValue();

      return;
    }

    if (index > 0) {
      digits.value[index - 1] = '';
      syncValue();
      focusInput(index - 1);
      event.preventDefault();
    }
  }

  if (event.key === 'ArrowLeft' && index > 0) {
    focusInput(index - 1);
    event.preventDefault();
  }

  if (event.key === 'ArrowRight' && index < props.digitsLength - 1) {
    focusInput(index + 1);
    event.preventDefault();
  }
};

const handlePaste = (event: ClipboardEvent, index: number): void => {
  event.preventDefault();

  const pastedText = event.clipboardData?.getData('text') ?? '';
  applyPastedDigits(pastedText, index);
};

const handleFocus = async (): Promise<void> => {
  if (model.value && model.value.length > 0) {
    return;
  }

  if (!navigator.clipboard?.readText) {
    return;
  }

  try {
    const clipboardText = await navigator.clipboard.readText();
    const sanitizedClipboard = clipboardText.replace(/\D/g, '');

    if (sanitizedClipboard.length !== props.digitsLength) {
      return;
    }

    applyPastedDigits(sanitizedClipboard, 0);
  } catch {
    return;
  }
};
</script>

<template>
	<div class="flex flex-col relative">
		<label class="font-medium mb-1 text-sky-700 block">
			{{ label }}
		</label>
		<Field v-model="codeValue" v-slot="{ errorMessage, meta }" :name="name" rules="required">
			<div class="flex items-center gap-2">
				<input
					v-for="(digit, index) in digits"
					:key="index"
					:ref="element => setInputRef(element as HTMLInputElement | null, index)"
					:value="digit"
					class="h-16 w-14 border-2 text-2xl text-center border-blue-100 outline-0"
					:class="[meta]"
					type="text"
					inputmode="numeric"
					pattern="[0-9]*"
					maxlength="1"
          v-autofocus="index === 0"
					:autocomplete="index === 0 ? 'one-time-code' : 'off'"
					@input="event => handleInput(event, index)"
					@keydown="event => handleKeydown(event, index)"
					@paste="event => handlePaste(event, index)"
          @focus="handleFocus"
				/>
			</div>
			<span v-if="errorMessage" class="error">{{ errorMessage }}</span>
		</Field>
	</div>
</template>
